import { useEffect, useState } from "react";
import api, { fmtMXN, fmtDate } from "../lib/api";
import Topbar from "../components/Topbar";
import PizzaSpinner from "../components/PizzaSpinner";
import EmptyState from "../components/EmptyState";
import { TrendingUp, TrendingDown, CheckCircle2, XCircle, Clock, Tag } from "lucide-react";
import { motion } from "framer-motion";
import { useAuth } from "../lib/auth";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";

export default function PriceChanges() {
  const { user } = useAuth();
  const { t } = useTranslation();
  const [list, setList] = useState(null);
  const [tab, setTab] = useState("pending");
  const [busy, setBusy] = useState(null);
  const isSupplier = user?.role === "supplier";

  const load = () => api.get("/price-changes").then((r) => setList(r.data));
  useEffect(() => { load(); }, []);

  const decide = async (pcrId, role, action, notes) => {
    setBusy(pcrId + role);
    try {
      await api.post(`/price-changes/${pcrId}/decision`, { role, action, notes });
      toast.success(action === "approve" ? t("priceChanges.approvedToast") : t("priceChanges.rejectedToast"));
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("common.error"));
    } finally {
      setBusy(null);
    }
  };

  if (!list) return <><Topbar title={t("priceChanges.title")} /><PizzaSpinner /></>;

  const filtered = list.filter((r) => tab === "all" ? true : r.status === tab);

  return (
    <>
      <Topbar title={isSupplier ? t("priceChanges.supplierTitle") : t("priceChanges.title")}
        subtitle={isSupplier ? t("priceChanges.supplierSubtitle") : t("priceChanges.subtitle")} />
      <div className="p-6 space-y-5">
        <div className="flex items-center gap-1.5 bg-white border border-slate-200 rounded-xl p-1 w-fit overflow-x-auto">
          {[
            { v: "pending", l: t("priceChanges.tabPending") },
            { v: "approved", l: t("priceChanges.tabApproved") },
            { v: "rejected", l: t("priceChanges.tabRejected") },
            { v: "all", l: t("priceChanges.tabAll") },
          ].map((tb) => (
            <button key={tb.v} data-testid={`pcr-tab-${tb.v}`} onClick={() => setTab(tb.v)}
              className={`px-3 py-1.5 text-xs font-semibold rounded-lg whitespace-nowrap ${tab === tb.v ? "bg-[#1A1A1A] text-white" : "text-slate-600 hover:bg-slate-100"}`}>
              {tb.l} ({list.filter(r => tb.v === "all" || r.status === tb.v).length})
            </button>
          ))}
        </div>

        {filtered.length === 0 ? (
          <EmptyState title={isSupplier ? t("priceChanges.supplierEmptyTitle") : t("priceChanges.emptyTitle")}
            message={isSupplier
              ? (tab === "pending" ? t("priceChanges.supplierEmptyMsg") : t("priceChanges.supplierEmptyOther"))
              : (tab === "pending" ? t("priceChanges.emptyPendingMsg") : t("priceChanges.emptyOtherMsg"))} icon={Tag} />
        ) : (
          <div className="grid gap-3">
            {filtered.map((r, idx) => {
              const up = r.proposed_price > r.previous_price;
              const isPending = r.status === "pending";
              const comprasOk = r.compras_approval?.decision === "approve";
              const direccionOk = r.direccion_approval?.decision === "approve";
              return (
                <motion.div key={r.id} initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: idx * 0.04 }}
                  className="bg-white rounded-2xl border border-slate-200 p-5" data-testid={`pcr-card-${r.id}`}>
                  <div className="flex items-start justify-between gap-4 flex-wrap">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <h3 className="font-display font-bold text-base text-[#1A1A1A]">{r.supply_name}</h3>
                        <span className="text-[10px] uppercase font-bold tracking-wider text-slate-500 bg-slate-100 px-2 py-0.5 rounded-full">{r.supply_unit}</span>
                        {r.status === "pending" && <span className="inline-flex items-center gap-1 text-xs font-semibold text-amber-700"><Clock className="w-3 h-3" /> {t("priceChanges.pendingTag")}</span>}
                        {r.status === "approved" && <span className="inline-flex items-center gap-1 text-xs font-semibold text-emerald-700"><CheckCircle2 className="w-3 h-3" /> {t("priceChanges.approvedTag")}</span>}
                        {r.status === "rejected" && <span className="inline-flex items-center gap-1 text-xs font-semibold text-red-700"><XCircle className="w-3 h-3" /> {t("priceChanges.rejectedTag")}</span>}
                      </div>
                      <div className="text-xs text-slate-500 mt-0.5">{r.supplier_name} · {t("priceChanges.requestedAt", { date: fmtDate(r.requested_at) })}</div>
                    </div>
                    <div className={`px-4 py-2 rounded-xl font-bold text-sm flex items-center gap-1.5 ${up ? "bg-red-50 text-red-700" : "bg-emerald-50 text-emerald-700"}`}>
                      {up ? <TrendingUp className="w-3.5 h-3.5" /> : <TrendingDown className="w-3.5 h-3.5" />}
                      {up ? "+" : ""}{r.change_percent}%
                    </div>
                  </div>

                  <div className="mt-4 grid grid-cols-2 gap-3">
                    <div className="bg-slate-50 rounded-lg p-3">
                      <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500">{t("priceChanges.currentPrice")}</div>
                      <div className="text-lg font-display font-extrabold font-mono mt-1">{fmtMXN(r.previous_price)}</div>
                    </div>
                    <div className={`rounded-lg p-3 ${up ? "bg-red-50" : "bg-emerald-50"}`}>
                      <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500">{t("priceChanges.proposed")}</div>
                      <div className={`text-lg font-display font-extrabold font-mono mt-1 ${up ? "text-red-700" : "text-emerald-700"}`}>{fmtMXN(r.proposed_price)}</div>
                    </div>
                  </div>

                  <div className="mt-3 text-sm text-slate-700">
                    <span className="text-[10px] uppercase tracking-wider font-bold text-slate-500 block mb-1">{t("priceChanges.justification")}</span>
                    {r.reason}
                  </div>

                  {r.status === "rejected" && r.rejection_reason && (
                    <div data-testid={`pcr-rejection-${r.id}`} className="mt-3 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800">
                      <span className="text-[10px] uppercase tracking-wider font-bold block mb-0.5">{t("priceChanges.rejectionReason")}</span>
                      {r.rejection_reason}
                    </div>
                  )}

                  {isSupplier && r.status === "pending" && (
                    <div className="mt-3 flex items-center gap-1.5 text-xs text-amber-700">
                      <Clock className="w-3.5 h-3.5" /> {t("priceChanges.statusPendingHint")}
                    </div>
                  )}

                  {user?.role === "corporate_admin" && (
                    <div className="mt-4 pt-4 border-t border-slate-100">
                      <div className="grid grid-cols-2 gap-3">
                        <div className={`rounded-xl p-3 border ${r.compras_approval ? (comprasOk ? "bg-emerald-50 border-emerald-200" : "bg-red-50 border-red-200") : "bg-amber-50 border-amber-200"}`}>
                          <div className="text-[10px] uppercase tracking-wider font-bold text-slate-600 mb-1">{t("priceChanges.compras")}</div>
                          {r.compras_approval ? (
                            <div className="text-xs font-semibold">
                              {comprasOk ? t("priceChanges.approvedTag") : t("priceChanges.rejectedTag")}
                              <div className="text-[10px] text-slate-500 mt-0.5">{r.compras_approval.approved_by_name}</div>
                            </div>
                          ) : (
                            isPending && (
                              <div className="flex gap-1.5 mt-1">
                                <button data-testid={`pcr-approve-compras-${r.id}`} disabled={busy === r.id + "compras"} onClick={() => decide(r.id, "compras", "approve")}
                                  className="flex-1 px-2 py-1 bg-emerald-500 text-white rounded-md text-[11px] font-bold disabled:opacity-50">{t("common.approve")}</button>
                                <button data-testid={`pcr-reject-compras-${r.id}`} disabled={busy === r.id + "compras"} onClick={() => decide(r.id, "compras", "reject", t("priceChanges.comprasRejects"))}
                                  className="flex-1 px-2 py-1 bg-red-500 text-white rounded-md text-[11px] font-bold disabled:opacity-50">{t("common.reject")}</button>
                              </div>
                            )
                          )}
                        </div>
                        <div className={`rounded-xl p-3 border ${r.direccion_approval ? (direccionOk ? "bg-emerald-50 border-emerald-200" : "bg-red-50 border-red-200") : "bg-amber-50 border-amber-200"}`}>
                          <div className="text-[10px] uppercase tracking-wider font-bold text-slate-600 mb-1">{t("priceChanges.direccion")}</div>
                          {r.direccion_approval ? (
                            <div className="text-xs font-semibold">
                              {direccionOk ? t("priceChanges.approvedTag") : t("priceChanges.rejectedTag")}
                              <div className="text-[10px] text-slate-500 mt-0.5">{r.direccion_approval.approved_by_name}</div>
                            </div>
                          ) : (
                            isPending && (
                              <div className="flex gap-1.5 mt-1">
                                <button data-testid={`pcr-approve-direccion-${r.id}`} disabled={busy === r.id + "direccion"} onClick={() => decide(r.id, "direccion", "approve")}
                                  className="flex-1 px-2 py-1 bg-emerald-500 text-white rounded-md text-[11px] font-bold disabled:opacity-50">{t("common.approve")}</button>
                                <button data-testid={`pcr-reject-direccion-${r.id}`} disabled={busy === r.id + "direccion"} onClick={() => decide(r.id, "direccion", "reject", t("priceChanges.direccionRejects"))}
                                  className="flex-1 px-2 py-1 bg-red-500 text-white rounded-md text-[11px] font-bold disabled:opacity-50">{t("common.reject")}</button>
                              </div>
                            )
                          )}
                        </div>
                      </div>
                    </div>
                  )}
                </motion.div>
              );
            })}
          </div>
        )}
      </div>
    </>
  );
}
